import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { ProjectConnectionCreateRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { projectConnectionMutations } from "@/queries/project-connections/mutations";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type FormValues, ProjectConnectionForm } from "./ProjectConnectionForm";

export function CreateProjectConnectionPage(): React.ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const { data: projects = [], isPending: isLoadingProjects, error: projectsError } = useLinkedProjects();
  const queries = useProjectConnectionQueries();
  const connectionsQuery = useQuery(queries.list({ Limit: 1000, Offset: 0, IncludeAllFromProfile: true }));
  const createConnection = projectConnectionMutations.useAddProjectConnection();

  const error = projectsError || connectionsQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (isLoadingProjects || connectionsQuery.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const handleSubmit = async (values: FormValues) => {
    if (values.backgroundImage.length === 0) {
      showFlashToast({ variant: "alert", message: t("page.project-connections.actions.create.notification.error") });

      return;
    }
    const image = await uploadFormImage(values.backgroundImage[0]);
    if (!image) {
      return;
    }
    const payload: ProjectConnectionCreateRequest = {
      name: values.name,
      description: values.description,
      content: values.content,
      contentTranslations: [],
      backgroundImageId: image.id,
      projectIds: values.projects.map((project) => project.id),
    };
    await createConnection.mutateAsync(payload);
    void navigate(routes.projectConnections.list());
  };

  return (
    <ProjectConnectionForm
      isEditMode={false}
      defaultFormValues={{}}
      projects={projects.map((project) => ({ ...project, baseLanguageId: project.projectBaseLanguageId }))}
      otherConnections={connectionsQuery.data?.items ?? []}
      isSubmitting={isUploadingImage || createConnection.isPending}
      onSubmit={handleSubmit}
    />
  );
}
